import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SuppliersService } from '../../../services/suppliers.service';
import { StatusService } from '../../../services/status.service';
import { Supplier } from '../../../models/Supplier';
import { Status } from '../../../models/Status';
import { TableAction } from '../../../shared/components/table/table.component';

@Component({
  selector: 'app-suppliers',
  templateUrl: './suppliers.component.html',
  styleUrls: ['./suppliers.component.css']
})
export class SuppliersComponent implements OnInit, OnDestroy {
  suppliers: Supplier[] = [];
  filteredSuppliers: Supplier[] = [];
  isLoading = false;
  loadingStatuses = false;

  // Paginação
  currentPage = 1;
  pageSize = 50;
  totalItems = 0;
  totalPages = 0;

  filters = {
    status: '',
    category: '',
    state: '',
    search: ''
  };

  private searchTimeout: any = null;

  statusOptions: { value: string; label: string }[] = [];
  categoryOptions: { value: string; label: string }[] = [];
  stateOptions: { value: string; label: string }[] = [];

  tableTitle = 'Listagem de Fornecedor';
  showSearch = true;
  searchPlaceholder = 'Buscar por razão social, CNPJ, cidade...';
  
  // Ações da tabela
  actions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  constructor(
    private router: Router, 
    private suppliersService: SuppliersService,
    private statusService: StatusService
  ) {}

  ngOnInit(): void {
    this.loadSupplierStatuses();
    this.loadSuppliers();
  }

  loadSuppliers(): void {
    this.isLoading = true;

    const hasClientFilters = !!(this.filters.status || this.filters.category || this.filters.state);

    const mapItem = (s: any) => ({ ...s, status: s.active ? 'Ativo' : 'Inativo' } as any);

    if (hasClientFilters) {
      const backendFilters: any = {};
      if (this.filters.search) backendFilters.search = this.filters.search;

      this.suppliersService.getAllPaged(1, 1000, backendFilters).subscribe({
        next: result => {
          const allMapped = result.items.map(mapItem);

          let filtered = allMapped;
          if (this.filters.status) {
            filtered = filtered.filter((s: any) => s.status === this.filters.status);
          }
          if (this.filters.category) {
            const cat = this.filters.category.toLowerCase();
            filtered = filtered.filter((s: any) =>
              (s.supplierCategory || '').toLowerCase() === cat
            );
          }
          if (this.filters.state) {
            const uf = this.filters.state.toUpperCase();
            filtered = filtered.filter((s: any) => (s.state || '').toUpperCase() === uf);
          }

          this.suppliers = allMapped;
          this.filteredSuppliers = filtered;
          this.totalItems = 0; // app-table handles internal pagination
          this.totalPages = 1;
          this.buildFilterOptions();
          this.isLoading = false;
        },
        error: _ => { this.isLoading = false; }
      });
    } else {
      const backendFilters: any = {};
      if (this.filters.search) backendFilters.search = this.filters.search;

      this.suppliersService.getAllPaged(this.currentPage, this.pageSize, backendFilters).subscribe({
        next: result => {
          this.suppliers = result.items.map(mapItem);
          this.filteredSuppliers = this.suppliers;
          this.totalItems = result.totalItems;
          this.totalPages = result.totalPages;
          this.currentPage = result.page;
          this.pageSize = result.pageSize;
          this.buildFilterOptions();
          this.isLoading = false;
        },
        error: _ => { this.isLoading = false; }
      });
    }
  }

  buildFilterOptions(): void {
    const categories = Array.from(new Set((this.suppliers || []).map(s => s.supplierCategory).filter(Boolean))) as string[];
    const states = [
      'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES',
      'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR',
      'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
      'SP', 'SE', 'TO'
    ];

    // Status já foi carregado dinamicamente em loadSupplierStatuses
    this.categoryOptions = [{ value: '', label: 'Todas as Categorias' }, ...categories.map(c => ({ value: c, label: c }))];
    this.stateOptions = [{ value: '', label: 'Todos os Estados' }, ...states.map(uf => ({ value: uf, label: uf }))];
  }

  loadSupplierStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('Supplier', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = [
            { value: '', label: 'Todos os Status' },
            ...statuses.map(status => ({
              value: status.name,
              label: status.name
            }))
          ];
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de fornecedores:', err);
          this.loadingStatuses = false;
          // Fallback para status básicos em caso de erro
          this.statusOptions = [
            { value: '', label: 'Todos os Status' },
            { value: 'Ativo', label: 'Ativo' },
            { value: 'Inativo', label: 'Inativo' }
          ];
        }
      });
  }

  applyFilters(): void {
    // Filtros agora são aplicados no backend, apenas recarrega os dados
    this.currentPage = 1;
    this.loadSuppliers();
  }

  clearFilters(): void {
    this.filters = { status: '', category: '', state: '', search: '' };
    this.currentPage = 1;
    this.loadSuppliers();
  }

  hasActiveFilters(): boolean {
    return !!(this.filters.status || this.filters.category || this.filters.state || this.filters.search);
  }

  /**
   * Handler para busca da tabela com debounce
   */
  onSearchChange(searchTerm: string): void {
    // Limpar timeout anterior se o usuário continuar digitando
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Atualiza o termo de busca imediatamente (para exibição no campo)
    this.filters.search = searchTerm;
    
    // Aguardar 600ms após parar de digitar antes de fazer a requisição
    this.searchTimeout = setTimeout(() => {
      this.currentPage = 1;
      this.loadSuppliers();
      this.searchTimeout = null;
    }, 600);
  }

  ngOnDestroy(): void {
    // Limpar timeout ao destruir componente
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  edit(supplier: Supplier): void { this.router.navigate(['/purchasing/suppliers', supplier.id, 'edit']); }

  newSupplier(): void {
    this.router.navigate(['/purchasing/suppliers/new']);
  }

  remove(id: number): void { this.suppliersService.delete(id).subscribe(() => this.loadSuppliers()); }

  onActionClick(actionData: { action: string; item: any }): void {
    const { action, item } = actionData;
    const supplier = item as Supplier;
    switch (action) {
      case 'edit':
        this.edit(supplier);
        break;
      case 'delete':
        if (confirm(`Tem certeza que deseja excluir o fornecedor "${supplier?.companyName || supplier?.id}"?`)) {
          this.remove(supplier.id);
        }
        break;
      default:
    }
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadSuppliers();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadSuppliers();
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.filteredSuppliers = [...this.filteredSuppliers].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  exportToCSV(): void {
    const cols = [
      { titulo: 'Razão Social', label: 'companyName' },
      { titulo: 'CNPJ', label: 'cnpj' },
      { titulo: 'Email', label: 'email' },
      { titulo: 'Telefone', label: 'primaryPhone' },
      { titulo: 'Status', label: 'status' },
      { titulo: 'Estado', label: 'state' },
      { titulo: 'Categoria', label: 'supplierCategory' }
    ];
    const header = cols.map(c => `"${c.titulo}"`).join(',');
    const rows = this.filteredSuppliers.map(item =>
      cols.map(c => `"${String((item as any)[c.label] ?? '').replace(/"/g, '""')}"`).join(',')
    );
    const csv = '﻿' + [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fornecedores.csv';
    a.click();
    URL.revokeObjectURL(url);
  }
}
